import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { generatePath } from '@/services/router/generate-path';
import { registerSiblingPreload, type SiblingPreloadContext } from '@/services/router/sibling-preload';

/**
 * The campaign overview page. Its Workflow tab moved to this app behind
 * `workflow-builder-migration` (FPS-2338) while its six siblings stayed with the
 * embedded app, so the common path onto the canvas is a click from an
 * embedded-app tab.
 *
 * Both URL spellings are matched — `campaigns` redirects to `automations`, so both
 * are live URLs — and the campaign id is bounded to digits, as campaign ids are
 * numeric.
 */
const CAMPAIGN_OVERVIEW = /^\/workspaces\/(last|\d+)\/journeys\/(?:campaigns|automations)\/(\d+)\/overview(?:[/?#]|$)/i;

/**
 * A person profile at its `journeys` twin, the address the embedded app's links still point
 * at (FPS-4208, FPS-2712). The profile's tabs are served by this app, so a click from such a link
 * warms the tabs it forwards to. The person id is any one segment: a profile is addressed by its
 * internal id or by an external id. `new`, `merge`, `create` and
 * `welcome` are People pages, not people, so they are excluded.
 */
const PERSON_PROFILE =
  /^\/workspaces\/(last|\d+)\/journeys\/people\/(?!(?:new|merge|create|welcome)(?:[/?#]|$))([^/?#]+)(?:[/?#]|$)/i;

/**
 * A transactional message's overview at its `journeys` twin, which the embedded app serves while
 * the message's tabs move one at a time. The message id is bounded to digits, as it is numeric.
 */
const TRANSACTIONAL_OVERVIEW = /^\/workspaces\/(last|\d+)\/journeys\/transactional\/(\d+)\/overview(?:[/?#]|$)/i;

/**
 * A segment's tab the embedded app still serves: its bare URL (Overview), Overview, People, Objects
 * or Ad Audiences. Usage moved to this app (FPS-4224), so a click from one of
 * these warms it. The segment id is bounded to digits, as segment ids are numeric.
 */
const SEGMENT_EMBEDDED_TAB =
  /^\/workspaces\/(last|\d+)\/journeys\/segments\/(\d+)(?:\/(?:overview|people|objects|audiences)(?:\/[^?#]*)?)?\/?(?:[?#]|$)/i;

/**
 * The segment tabs the embedded app serves beside Ad Audiences, which moved to this app
 * (FPS-4299): the bare URL (Overview), Overview, People, Objects and Usage at its `journeys`
 * URL. A click from one of these warms Ad Audiences.
 */
const SEGMENT_AUDIENCES_SIBLING =
  /^\/workspaces\/(last|\d+)\/journeys\/segments\/(\d+)(?:\/(?:overview|people|objects|usage)(?:\/[^?#]*)?)?\/?(?:[?#]|$)/i;

type TransactionalOverviewParams = { workspaceId: string; transactionalMessageId: string };

type TransactionalOverviewTab = {
  /** The tab's rollout flag, while it has one. */
  flag?: AccountFeatureFlag;
  href: (params: TransactionalOverviewParams) => string;
};

/** The overview tabs this app serves: the Overview tab for every account, the rest behind their flags. */
const TRANSACTIONAL_OVERVIEW_TABS: readonly TransactionalOverviewTab[] = [
  {
    href: (params: TransactionalOverviewParams) =>
      generatePath('/workspaces/:workspaceId/transactional/:transactionalMessageId/overview', params),
  },
  {
    flag: AccountFeatureFlag.TransactionalSentTabMigration,
    href: (params: TransactionalOverviewParams) =>
      generatePath('/workspaces/:workspaceId/transactional/:transactionalMessageId/overview/sent', params),
  },
  {
    flag: AccountFeatureFlag.TransactionalDraftsTabMigration,
    href: (params: TransactionalOverviewParams) =>
      generatePath('/workspaces/:workspaceId/transactional/:transactionalMessageId/overview/drafts', params),
  },
  {
    flag: AccountFeatureFlag.TransactionalSendTabMigration,
    href: (params: TransactionalOverviewParams) =>
      generatePath('/workspaces/:workspaceId/transactional/:transactionalMessageId/overview/send', params),
  },
  {
    flag: AccountFeatureFlag.TransactionalMetricsTabMigration,
    href: (params: TransactionalOverviewParams) =>
      generatePath('/workspaces/:workspaceId/transactional/:transactionalMessageId/overview/metrics', params),
  },
];

/**
 * An API-triggered broadcast's overview, at its `journeys` twin, where the embedded app still serves
 * the tabs this app does not, such as the workflow, or at this app's address, one of its own tabs.
 * From a tab of this app's, the rest are warmed so a switch between them keeps the tab bar on screen
 * instead of waiting on a chunk under a page-wide loader. The broadcast id is bounded to digits, as
 * it is numeric.
 */
const BROADCAST_OVERVIEW =
  /^\/workspaces\/(last|\d+)\/(?:journeys\/)?broadcasts\/broadcast\/(\d+)\/overview(?:[/?#]|$)/i;

/** The broadcast tabs this app serves. */
const BROADCAST_OVERVIEW_TAB_ROUTES = [
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview',
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview/triggering_details',
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview/metrics',
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview/tests',
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview/drafts',
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview/broadcasts',
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview/journeys',
  '/workspaces/:workspaceId/broadcasts/broadcast/:broadcastId/overview/form-submissions',
] as const;

/** Warms the automation or broadcast the overview frame and every tab read. */
async function prefetchCampaign({ queryClient, match }: SiblingPreloadContext) {
  const [, workspaceSegment, campaignId] = match;
  // `last` is resolved by the workspace resolver, not here; with no numeric id there is no cache
  // key to write, so the chunk is all this can warm.
  if (workspaceSegment == null || campaignId == null || workspaceSegment.toLowerCase() === 'last') {
    return;
  }

  // Imported here so the shell entry bundle does not grow by the campaign data layer.
  const { getCampaignQueryOptions } = await import('@/services/db/campaign/queries');
  await queryClient.prefetchQuery(getCampaignQueryOptions(Number(workspaceSegment), campaignId, queryClient));
}

/** Warms the message the overview frame and its tabs read. */
async function prefetchTransactionalMessage({ queryClient, match }: SiblingPreloadContext) {
  const [, workspaceSegment, transactionalMessageId] = match;
  // `last` is resolved by the workspace resolver, not here; with no numeric id there is no cache
  // key to write, so the chunk is all this can warm.
  if (workspaceSegment == null || transactionalMessageId == null || workspaceSegment.toLowerCase() === 'last') {
    return;
  }

  // Imported here so the shell entry bundle does not grow by the transactional data layer.
  const { getTransactionalMessageQueryOptions } = await import('@/services/db/transactional-message/queries');
  await queryClient.prefetchQuery(
    getTransactionalMessageQueryOptions(Number(workspaceSegment), transactionalMessageId, queryClient),
  );
}

const SEGMENT_TAB_ROUTES = {
  usage: '/workspaces/:workspaceId/segments/:segmentId/usage',
  audiences: '/workspaces/:workspaceId/segments/:segmentId/audiences',
} as const;

type SegmentTabData = (
  queryClient: SiblingPreloadContext['queryClient'],
  workspaceId: number,
  segmentId: string,
) => Promise<void>;

/**
 * Preloads a Hydra-owned segment tab from an embedded sibling: the tab's chunk, the segment the frame
 * reads, and the tab's own data.
 */
function registerSegmentTabPreload(when: RegExp, tab: 'usage' | 'audiences', prefetchTab: SegmentTabData) {
  registerSiblingPreload({
    when,

    siblings: (match) => {
      const [, workspaceId, segmentId] = match;
      return [generatePath(SEGMENT_TAB_ROUTES[tab], { workspaceId, segmentId })];
    },

    prefetchData: async ({ queryClient, match }) => {
      const [, workspaceSegment, segmentId] = match;
      // `last` is resolved by the workspace resolver, not here; with no numeric id there is no
      // cache key to write, so the chunk is all this can warm.
      if (workspaceSegment == null || segmentId == null || workspaceSegment.toLowerCase() === 'last') return;

      // Imported here so the shell entry bundle does not grow by the segment data layer.
      const { getSegmentQueryOptions } = await import('@/services/db/segment/queries');
      const workspaceId = Number(workspaceSegment);
      await Promise.all([
        queryClient.prefetchQuery(getSegmentQueryOptions(workspaceId, segmentId, queryClient)),
        prefetchTab(queryClient, workspaceId, segmentId),
      ]);
    },
  });
}

/** The URL segment decoded, or `null` when it is not valid percent-encoding. */
function decodeSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Register every sibling preload. Called from `main.tsx`, next to
 * `installLinkPrefetching`: the entry module runs before `render`, and the shell
 * preloads on its first location effect, so an entry registered any later would
 * miss the load the user actually landed on.
 */
export function registerSiblingPreloads() {
  registerSiblingPreload({
    when: PERSON_PROFILE,
    siblings: (match) => {
      const [, workspaceId, personId] = match;
      return [
        generatePath('/workspaces/:workspaceId/people/:personId/attributes', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/relationships', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/segments/belongs', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/devices', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/drafts', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/sent', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/activity', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/journeys', { workspaceId, personId }),
      ];
    },

    prefetchData: async ({ queryClient, match }) => {
      const [, workspaceSegment, personSegment] = match;
      // The page reads the person by the decoded id, so an encoded external id must be decoded
      // to warm the same cache key.
      const personId = personSegment == null ? null : decodeSegment(personSegment);
      // `last` is resolved by the workspace resolver, not here; with no numeric id there is no
      // cache key to write, so the chunk is all this can warm.
      if (workspaceSegment == null || personId == null || workspaceSegment.toLowerCase() === 'last') return;

      // Imported here so the shell entry bundle does not grow by the customer data layer.
      const { getCustomerQueryOptions } = await import('@/services/db/customer/queries');
      await queryClient.prefetchQuery(getCustomerQueryOptions(Number(workspaceSegment), personId));
    },
  });

  registerSiblingPreload({
    when: CAMPAIGN_OVERVIEW,
    flag: AccountFeatureFlag.WorkflowBuilderMigration,

    siblings: (match) => {
      const [, workspaceId, campaignId] = match;
      // The canonical address. The legacy `.../overview/workflow/actions` twin forwards
      // to it, so one href covers both: the chunk loader and the query cache are
      // shared, and ownership answers the same for either shape.
      return [generatePath('/workspaces/:workspaceId/automations/:campaignId/workflow', { workspaceId, campaignId })];
    },

    prefetchData: async ({ queryClient, match }) => {
      const [, workspaceSegment, campaignId] = match;
      // `last` is resolved by the workspace resolver, not here; with no numeric
      // id there is no cache key to write, so the chunk is all this can warm.
      if (workspaceSegment == null || campaignId == null || workspaceSegment.toLowerCase() === 'last') return;

      // Imported here rather than at module scope so the shell entry bundle does
      // not grow by the campaign data layer, and so an account with the rollout
      // flag off never loads it at all.
      const { getCampaignWorkflowDetailQueryOptions } = await import('@/services/db/campaign/queries');

      // One `GET /campaigns/:id`. Its `populateCampaignModel` fans the envelope
      // into `campaignKeys.detail` and every action detail slot as well as the
      // workflow-detail slot, so this single prefetch also satisfies `useCampaign`
      // and `useCampaignActions` — the page's other two suspense sources.
      await queryClient.prefetchQuery(
        getCampaignWorkflowDetailQueryOptions(Number(workspaceSegment), campaignId, queryClient),
      );
    },
  });

  registerSegmentTabPreload(SEGMENT_EMBEDDED_TAB, 'usage', async (queryClient, workspaceId, segmentId) => {
    const { getSegmentUsageQueryOptions } = await import('@/services/db/segment/queries');
    await queryClient.prefetchQuery(getSegmentUsageQueryOptions(workspaceId, segmentId));
  });

  registerSegmentTabPreload(SEGMENT_AUDIENCES_SIBLING, 'audiences', async (queryClient, workspaceId) => {
    const [{ getAudiencesQueryOptions }, { getAudienceCredentialsQueryOptions }] = await Promise.all([
      import('@/services/db/audience/queries'),
      import('@/services/db/audience-credential/queries'),
    ]);
    await Promise.all([
      queryClient.prefetchQuery(getAudiencesQueryOptions(workspaceId, queryClient)),
      queryClient.prefetchQuery(getAudienceCredentialsQueryOptions(workspaceId, queryClient)),
    ]);
  });

  // An automation's Overview tab (FPS-4264), from the tabs the embedded app still serves beside it.
  registerSiblingPreload({
    when: CAMPAIGN_OVERVIEW,
    flag: AccountFeatureFlag.CampaignOverviewTabMigration,

    siblings: (match) => {
      const [, workspaceId, campaignId] = match;
      return [generatePath('/workspaces/:workspaceId/automations/:campaignId/overview', { workspaceId, campaignId })];
    },

    prefetchData: prefetchCampaign,
  });

  registerSiblingPreload({
    when: BROADCAST_OVERVIEW,

    siblings: (match) => {
      const [, workspaceId, broadcastId] = match;
      return BROADCAST_OVERVIEW_TAB_ROUTES.map((route) => generatePath(route, { workspaceId, broadcastId }));
    },

    prefetchData: prefetchCampaign,
  });

  for (const tab of TRANSACTIONAL_OVERVIEW_TABS) {
    registerSiblingPreload({
      when: TRANSACTIONAL_OVERVIEW,
      flag: tab.flag,

      siblings: (match) => {
        const [, workspaceId, transactionalMessageId] = match;
        return [tab.href({ workspaceId, transactionalMessageId })];
      },

      prefetchData: prefetchTransactionalMessage,
    });
  }
}
